/**
 * BLOCK 70 Part B ,  Admin platform dashboard.
 *
 * Renders instead of the subscriber Dashboard when the current user's
 * role is admin, super_admin, or manager. Fully read-only. Surfaces
 * exclusively metrics that are already tracked elsewhere in the app , 
 * this page adds NO new instrumentation, NO new writes, and it never
 * recomputes anything expensive on the server. Data comes from a single
 * aggregator endpoint: GET /api/admin/dashboard/summary.
 *
 * The six tiles map 1:1 to the block-70 acceptance list:
 *   - Live opportunities (is_test excluded)
 *   - Active subscribers by tier (from the users collection)
 *   - Pending review queue (same field the sidebar badge polls)
 *   - Connector health summary (buckets match the connector card)
 *   - Webhook health summary (same fields as the webhook card)
 *   - Recent audit log activity (last 15, actor-enriched)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Users, ClipboardList, Radio, Webhook, ScrollText,
  RefreshCw, ExternalLink, ShieldCheck, HardDrive, AlertTriangle, Mail,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { tierLabel } from "@/lib/tiers";

// BLOCK 72 Item 4 ,  tier keys/labels come from the single source of truth
// in lib/tiers.js so the retired legacy tier name can never leak back
// into this file (the CI grep in scripts/check_frontend_stale_terms.sh
// would fail the build the moment it did). We show the three canonical
// user-facing tiers only; any legacy backend aliases collapse to
// "Business" via tierLabel().
const TIER_ORDER = ["free", "pro", "business"];
const BUCKET_META = {
  green:  { label: "Green",  dot: "bg-emerald-500", color: "text-emerald-400" },
  amber:  { label: "Amber",  dot: "bg-amber-500",   color: "text-amber-400" },
  red:    { label: "Red",    dot: "bg-red-500",     color: "text-red-400" },
  silent: { label: "Silent", dot: "bg-slate-500",   color: "text-slate-400" },
  // BLOCK 178 Item 3 ,  a managed database whose filesystem isn't
  // measurable from in here. Deliberately NOT red/amber ,  this is an
  // honest "can't tell", not a known-bad reading.
  unknown: { label: "Unknown", dot: "bg-sky-500",   color: "text-sky-400" },
};

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : ", ");

function StatBig({ label, value, sub, testid }) {
  return (
    <div className="rounded-sm border border-white/10 bg-[#0A0D12] px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono tabular-nums text-3xl mt-1 text-slate-100">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ActionLink({ to, children, testid }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-emerald-400 hover:text-emerald-300"
      data-testid={testid}
    >
      {children}<ExternalLink className="h-3 w-3" />
    </Link>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/dashboard/summary");
      setData(r.data); setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-10 text-slate-400" data-testid="admin-dashboard-loading">
        Loading platform snapshot…
      </div>
    );
  }
  if (err) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-10 text-red-300" data-testid="admin-dashboard-error">
        {err}
      </div>
    );
  }

  const opps = data.opportunities || {};
  const subs = data.subscribers || { total: 0, by_tier: {} };
  const cons = data.connectors || {};
  const hooks = data.webhooks || {};
  const audits = data.recent_audit || [];
  const disk = data.disk || {};
  const emailHealth = data.email_health || { total_sent_24h: 0, total_error_24h: 0, by_purpose: {}, bucket: "silent", blocked_bounced_7d: [], blocked_alert_recipients: [] };
  const emailMeta = BUCKET_META[emailHealth.bucket] || BUCKET_META.silent;
  const diskBucket = disk.bucket || "silent";
  const diskMeta = BUCKET_META[diskBucket] || BUCKET_META.silent;
  const backupNote = disk.latest_backup?.note || "";
  const backupStatus = disk.latest_backup?.status || "unknown";
  // BLOCK 76 ,  surface the Skipped_low_disk / Failed signal loud on the tile
  // instead of leaving it buried in the data_pull_logs collection.
  const backupTrouble =
    /skipped_low_disk/i.test(backupNote) ||
    /skipped/i.test(backupStatus) ||
    /fail/i.test(backupStatus);
  const skippedLowDisk = /skipped_low_disk/i.test(backupNote) || /skipped/i.test(backupStatus);
  const tierRows = TIER_ORDER
    .map((t) => ({ tier: t, n: subs.by_tier?.[t] || 0 }))
    .concat(
      Object.keys(subs.by_tier || {})
        .filter((t) => !TIER_ORDER.includes(t))
        .map((t) => ({ tier: t, n: subs.by_tier[t] }))
    );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-dashboard-view">
      {/* Header */}
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1 flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" /> Admin · platform view
          </div>
          <h1 className="text-3xl font-semibold text-slate-100">Acquigine platform dashboard</h1>
          <div className="text-xs text-slate-500 mt-1">
            Read-only snapshot · as-of {fmtDateTime(data.as_of)}
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-300 hover:text-slate-100 border border-white/10 px-3 py-1.5 rounded-sm disabled:opacity-50"
          data-testid="admin-dashboard-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Row 1 ,  big platform counters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <StatBig
          label="Live opportunities"
          value={fmt(opps.total)}
          sub={`${fmt(opps.active)} active · ${fmt(opps.archived)} archived`}
          testid="stat-opps-total"
        />
        <StatBig
          label="Pending review"
          value={fmt(opps.pending_review)}
          sub="submission_status = pending_review"
          testid="stat-pending-review"
        />
        <StatBig
          label="Active subscribers"
          value={fmt(subs.total)}
          sub={`${tierRows.length} tier${tierRows.length === 1 ? "" : "s"}`}
          testid="stat-active-subs"
        />
        <StatBig
          label="Webhook events · 24h"
          value={fmt(hooks.brevo_events_24h)}
          sub={`${fmt(hooks.webhook_rejections_24h)} rejections · brevo token ${hooks.brevo_token_configured ? "configured" : "missing"}`}
          testid="stat-webhook-24h"
        />
      </div>

      {/* Row 2 ,  subscribers by tier + connector bucket */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Subscribers by tier */}
        <div className="rounded-sm border border-white/10 bg-[#0A0D12] p-5" data-testid="card-subs-by-tier">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Users className="h-3 w-3" /> Subscribers by tier
            </div>
            <ActionLink to="/app/users" testid="link-users">Manage</ActionLink>
          </div>
          {tierRows.length === 0 ? (
            <div className="text-sm text-slate-500">No active subscribers.</div>
          ) : (
            <div className="space-y-1.5">
              {tierRows.map((t) => (
                <div key={t.tier}
                     className="flex items-center justify-between border-b border-white/6 last:border-0 py-1.5"
                     data-testid={`tier-row-${t.tier}`}>
                  <div className="text-sm text-slate-200">{tierLabel(t.tier)}</div>
                  <div className="font-mono tabular-nums text-lg text-slate-100">{fmt(t.n)}</div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 text-[11px] text-slate-500">
                <span>Filter: role=specialist, active=true, is_test=false</span>
                <span>Total <span className="text-slate-300 font-mono">{fmt(subs.total)}</span></span>
              </div>
            </div>
          )}
        </div>

        {/* Connector health */}
        <div className="rounded-sm border border-white/10 bg-[#0A0D12] p-5" data-testid="card-connector-health">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Radio className="h-3 w-3" /> Connector health
            </div>
            <ActionLink to="/app/admin/discovery/configs" testid="link-connectors">Detail</ActionLink>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {["red", "amber", "green", "silent"].map((b) => (
              <div key={b} className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5"
                   data-testid={`connector-bucket-${b}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                  <span className={`inline-block h-2 w-2 rounded-full ${BUCKET_META[b].dot}`} />
                  {BUCKET_META[b].label}
                </div>
                <div className={`font-mono tabular-nums text-2xl mt-1 ${BUCKET_META[b].color}`}>
                  {fmt((cons.counts || {})[b] || 0)}
                </div>
                {/* BLOCK 71 Item 2 ,  split the Red count so nobody reads it as
                    "144 things are broken". Manual-batch/CSV connectors stay
                    red by design (they can't self-heal without a fresh upload),
                    so the actionable failure count is `red - manual_batch`. */}
                {b === "red" && (cons.counts?.red || 0) > 0 && (
                  <div className="text-[10px] text-slate-500 mt-1 leading-snug"
                       data-testid="connector-red-breakdown">
                    <span className="text-slate-400">{fmt(cons.red_manual_batch || 0)}</span> batch/CSV
                    <span className="text-slate-600"> by design</span>
                    {" · "}
                    <span className={cons.red_needs_intervention ? "text-red-300" : "text-slate-400"}>
                      {fmt(cons.red_needs_intervention || 0)}
                    </span> needs attention
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-3">
            Buckets classified by the same rule the Connector Health card uses.
          </div>
        </div>
      </div>

      {/* Row 3 ,  webhook + pending review actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="rounded-sm border border-white/10 bg-[#0A0D12] p-5" data-testid="card-webhook-health">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Webhook className="h-3 w-3" /> Webhook health · 24h
            </div>
            <ActionLink to="/app/admin/platform-ops" testid="link-webhook">Detail</ActionLink>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Brevo events</div>
              <div className="font-mono tabular-nums text-2xl mt-1 text-emerald-400">{fmt(hooks.brevo_events_24h)}</div>
            </div>
            <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Rejections</div>
              <div className={`font-mono tabular-nums text-2xl mt-1 ${hooks.webhook_rejections_24h ? "text-red-400" : "text-slate-300"}`}>
                {fmt(hooks.webhook_rejections_24h)}
              </div>
            </div>
            <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Brevo token</div>
              <div className={`font-mono text-sm mt-2 uppercase tracking-widest ${hooks.brevo_token_configured ? "text-emerald-400" : "text-red-400"}`}>
                {hooks.brevo_token_configured ? "configured" : "missing"}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-sm border border-white/10 bg-[#0A0D12] p-5" data-testid="card-pending-review">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <ClipboardList className="h-3 w-3" /> Pending review queue
            </div>
            <ActionLink to="/app/admin/submissions" testid="link-pending">Review</ActionLink>
          </div>
          <div className="flex items-baseline gap-4">
            <div className="font-mono tabular-nums text-5xl text-slate-100">{fmt(opps.pending_review)}</div>
            <div className="text-sm text-slate-500">submissions awaiting an admin decision.</div>
          </div>
          <div className="text-[11px] text-slate-500 mt-3">
            Same field the sidebar badge counts ,  <code className="text-slate-400">submission_status = pending_review</code>.
          </div>
        </div>
      </div>

      {/* Row 3.4 ,  BLOCK 170 Item C: email delivery health */}
      <div className="grid grid-cols-1 mb-6">
        <div className="rounded-sm border border-white/10 bg-[#0A0D12] p-5" data-testid="card-email-health">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Mail className="h-3 w-3" /> Email delivery health · 24h
            </div>
            <div className="text-[10px] uppercase tracking-widest flex items-center gap-1.5" data-testid="email-health-bucket-badge">
              <span className={`inline-block h-2 w-2 rounded-full ${emailMeta.dot}`} />
              <span className={emailMeta.color}>{emailMeta.label}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5" data-testid="email-sent-24h">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Sent</div>
              <div className="font-mono tabular-nums text-2xl mt-1 text-emerald-400">{fmt(emailHealth.total_sent_24h)}</div>
            </div>
            <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5" data-testid="email-error-24h">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Failed</div>
              <div className={`font-mono tabular-nums text-2xl mt-1 ${emailHealth.total_error_24h ? "text-red-400" : "text-slate-300"}`}>
                {fmt(emailHealth.total_error_24h)}
              </div>
            </div>
          </div>
          {Object.keys(emailHealth.by_purpose || {}).length > 0 && (
            <div className="space-y-1" data-testid="email-health-by-purpose">
              {Object.entries(emailHealth.by_purpose).map(([purpose, counts]) => (
                <div key={purpose} className="flex items-center justify-between border-b border-white/6 last:border-0 py-1.5 text-[11px]">
                  <span className="font-mono text-slate-400">{purpose}</span>
                  <span>
                    <span className="text-emerald-400 font-mono">{fmt(counts.sent)}</span> sent
                    {counts.error > 0 && <span className="text-red-400 font-mono ml-2">{fmt(counts.error)} failed</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
          {emailHealth.total_error_24h > 0 && (
            <div className="text-[11px] text-red-300 mt-3 flex items-center gap-1.5" data-testid="email-health-warning">
              <AlertTriangle className="h-3 w-3" /> Failures in the last 24h ,  check the provider's IP allowlist and API key.
            </div>
          )}

          {/* BLOCK 171 Item 2 ,  alert recipients currently blocked/bounced at the provider */}
          {emailHealth.blocked_alert_recipients?.length > 0 && (
            <div className="mt-3 p-2.5 rounded-sm border border-red-500/40 bg-red-500/10 text-[11px] text-red-300 flex items-center gap-1.5" data-testid="email-health-alert-recipient-blocked-banner">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Submission alerts are being silently lost ,  {emailHealth.blocked_alert_recipients.join(", ")} is currently blocked/bounced at the provider.
            </div>
          )}

          {/* BLOCK 171 Item 2 ,  blocked/bounced recipients, 7d (provider-confirmed ground truth via the Brevo webhook) */}
          {emailHealth.blocked_bounced_7d?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/8" data-testid="email-health-blocked-bounced-7d">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Blocked &amp; bounced · 7d</div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {emailHealth.blocked_bounced_7d.map((ev, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] border-b border-white/6 last:border-0 py-1">
                    <span className="text-slate-300 truncate max-w-[55%]">{ev.email}</span>
                    <span className="text-red-400 font-mono uppercase text-[10px]">{ev.event}</span>
                    <span className="text-slate-500 truncate max-w-[30%]" title={ev.reason}>{ev.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row 3.5 ,  BLOCK 76 disk-health tile */}
      <div className="grid grid-cols-1 mb-6">
        <div className="rounded-sm border border-white/10 bg-[#0A0D12] p-5" data-testid="card-disk-health">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <HardDrive className="h-3 w-3" /> Disk health
            </div>
            <div className="text-[10px] uppercase tracking-widest flex items-center gap-1.5" data-testid="disk-bucket-badge">
              <span className={`inline-block h-2 w-2 rounded-full ${diskMeta.dot}`} />
              <span className={diskMeta.color}>{diskMeta.label}</span>
            </div>
          </div>

          {disk.bucket === "unknown" ? (
            <div data-testid="disk-health-unknown">
              <div className="text-sm text-sky-300">
                {disk.message || "Managed database ,  disk usage isn't measurable from this app."}
              </div>
              <div className="flex items-center justify-between border-t border-white/8 pt-3 mt-3" data-testid="disk-backup-signal">
                <div className="text-[11px] text-slate-500">
                  Latest MongoDB backup
                  {disk.latest_backup?.created_at && (
                    <span className="text-slate-400"> · {fmtDateTime(disk.latest_backup.created_at)}</span>
                  )}
                </div>
                <div
                  className={`text-[11px] uppercase tracking-widest font-mono flex items-center gap-1.5 ${
                    backupTrouble ? "text-red-400" : "text-emerald-400"
                  }`}
                  data-testid="disk-backup-status"
                >
                  {backupTrouble && <AlertTriangle className="h-3 w-3" />}
                  {skippedLowDisk ? "Skipped_low_disk" : backupStatus}
                </div>
              </div>
              {disk.backup_note && (
                <div className="text-[11px] text-slate-500 mt-2" data-testid="disk-backup-note">
                  {disk.backup_note}
                </div>
              )}
            </div>
          ) : disk.error ? (
            <div className="text-sm text-red-300" data-testid="disk-health-error">
              Unable to read disk usage: {disk.error}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5" data-testid="disk-free">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Free</div>
                  <div className={`font-mono tabular-nums text-2xl mt-1 ${diskMeta.color}`}>
                    {fmt(Math.round(disk.free_mib))} <span className="text-sm text-slate-500">MiB</span>
                  </div>
                </div>
                <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5" data-testid="disk-used-pct">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Used</div>
                  <div className="font-mono tabular-nums text-2xl mt-1 text-slate-100">
                    {typeof disk.used_pct === "number" ? disk.used_pct.toFixed(1) : ", "}<span className="text-sm text-slate-500">%</span>
                  </div>
                </div>
                <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5" data-testid="disk-total">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Total</div>
                  <div className="font-mono tabular-nums text-2xl mt-1 text-slate-300">
                    {fmt(Math.round(disk.total_mib))} <span className="text-sm text-slate-500">MiB</span>
                  </div>
                </div>
                <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2.5" data-testid="disk-gate">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Safety gate</div>
                  <div className={`font-mono text-sm mt-2 uppercase tracking-widest ${diskMeta.color}`}>
                    {disk.reason || ", "}
                  </div>
                </div>
              </div>

              {/* usage bar */}
              <div className="h-2 w-full rounded-full bg-black/40 overflow-hidden mb-3" data-testid="disk-usage-bar">
                <div
                  className={`h-full ${diskBucket === "red" ? "bg-red-500" : diskBucket === "amber" ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, Math.max(0, disk.used_pct || 0))}%` }}
                />
              </div>

              {/* latest backup / Skipped_low_disk signal */}
              <div className="flex items-center justify-between border-t border-white/8 pt-3" data-testid="disk-backup-signal">
                <div className="text-[11px] text-slate-500">
                  Latest MongoDB backup
                  {disk.latest_backup?.created_at && (
                    <span className="text-slate-400"> · {fmtDateTime(disk.latest_backup.created_at)}</span>
                  )}
                </div>
                <div
                  className={`text-[11px] uppercase tracking-widest font-mono flex items-center gap-1.5 ${
                    backupTrouble ? "text-red-400" : "text-emerald-400"
                  }`}
                  data-testid="disk-backup-status"
                >
                  {backupTrouble && <AlertTriangle className="h-3 w-3" />}
                  {skippedLowDisk ? "Skipped_low_disk" : backupStatus}
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-2">
                Red &lt; 500 MiB (multiplanner gate) · Amber &lt; 1 GiB (backup floor) · same rule worker.py enforces.
              </div>
              {disk.backup_note && (
                <div className="text-[11px] text-slate-500 mt-1" data-testid="disk-backup-note">
                  {disk.backup_note}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Row 4 ,  recent audit log */}
      <div className="rounded-sm border border-white/10 bg-[#0A0D12] p-5" data-testid="card-recent-audit">
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <ScrollText className="h-3 w-3" /> Recent audit activity <span className="text-slate-600">· last 15</span>
          </div>
          <div className="text-[11px] text-slate-500 flex items-center gap-1"><Activity className="h-3 w-3" /> live</div>
        </div>
        {audits.length === 0 ? (
          <div className="text-sm text-slate-500">No recent audit entries.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-recent-audit">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
                  <th className="text-left py-2 pr-3">When</th>
                  <th className="text-left py-2 pr-3">Actor</th>
                  <th className="text-left py-2 pr-3">Action</th>
                  <th className="text-left py-2 pr-3">Target</th>
                </tr>
              </thead>
              <tbody>
                {audits.map((a) => (
                  <tr key={a.aid} className="border-b border-white/6 last:border-0" data-testid={`audit-row-${a.aid}`}>
                    <td className="py-2 pr-3 font-mono text-[11px] text-slate-400">
                      {a.created_at ? fmtDateTime(a.created_at) : ", "}
                    </td>
                    <td className="py-2 pr-3 text-slate-200">
                      {a.actor?.name || a.actor?.email || <span className="text-slate-500">(system)</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-emerald-400">{a.action || ", "}</td>
                    <td className="py-2 pr-3 font-mono text-[11px] text-slate-500">
                      {a.target_type ? `${a.target_type}:${(a.target_id || "").slice(0, 12)}` : ", "}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
